/**
 * Attack Simulation Training API client.
 *
 * Wraps the Hyperion backend (`/api/v1/attacksim/...`) which proxies
 * Microsoft Graph attack simulation endpoints for a target M365 tenant.
 */

import { api } from '@/platform/api-client/client';

export interface Simulation {
  id: string;
  displayName: string;
  description?: string;
  status: string;
  attackType?: string;
  attackTechnique?: string;
  payloadDeliveryPlatform?: string;
  createdBy?: string;
  createdDateTime?: string;
  launchDateTime?: string;
  completionDateTime?: string;
  includedAccountTarget?: number;
  numberOfCompromisedUsers?: number;
}

export interface SimulationListResponse {
  data: Simulation[];
  total: number;
  status?: 'ok' | 'entitlement_missing' | 'error' | string;
  detail?: string;
}

export interface CreateSimulationInput {
  displayName: string;
  description?: string;
  attackTechnique: string;
  attackType?: string;
  payloadDeliveryPlatform: string;
  payloadId?: string;
  loginPageId?: string;
  landingPageId?: string;
  includedUserIds?: string[];
  excludedUserIds?: string[];
  launchDateTime?: string;
  durationInDays?: number;
}

export interface CoverageEntry {
  userId: string;
  userPrincipalName: string;
  displayName: string;
  simulationCount: number;
  trainingCompletionCount: number;
  lastSimulationDateTime?: string;
}

export interface CoverageResponse {
  data: CoverageEntry[];
  total: number;
  status?: string;
  detail?: string;
}

export interface RepeatOffender {
  userId: string;
  userPrincipalName: string;
  displayName: string;
  compromisedCount: number;
  simulationCount: number;
}

export interface RepeatOffenderResponse {
  data: RepeatOffender[];
  total: number;
  status?: string;
  detail?: string;
}

const base = (tenantId: string) => `/attacksim/${encodeURIComponent(tenantId)}`;

export async function listSimulations(tenantId: string): Promise<SimulationListResponse> {
  const { data } = await api.get<SimulationListResponse>(`${base(tenantId)}/simulations`);
  return data;
}

export async function getSimulation(tenantId: string, simId: string): Promise<Simulation> {
  const { data } = await api.get<Simulation>(`${base(tenantId)}/simulations/${encodeURIComponent(simId)}`);
  return data;
}

export async function createSimulation(tenantId: string, input: CreateSimulationInput): Promise<Simulation> {
  const { data } = await api.post<Simulation>(`${base(tenantId)}/simulations`, input);
  return data;
}

export async function getCoverage(tenantId: string): Promise<CoverageResponse> {
  const { data } = await api.get<CoverageResponse>(`${base(tenantId)}/reports/coverage`);
  return data;
}

export async function getRepeatOffenders(tenantId: string): Promise<RepeatOffenderResponse> {
  const { data } = await api.get<RepeatOffenderResponse>(`${base(tenantId)}/reports/repeat-offenders`);
  return data;
}
